// @flow

import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as Cookie from 'app/packages/cookie-utils';
import CreatorChecklistAction from 'app/common/react/components/CreatorChecklist/CreatorChecklistAction';
import i18n from 'app/common/lib/i18n';
import resourceFactory from 'app/common/lib/ResourceFactory';
import UserWebsiteInput from 'app/common/react/components/CreatorChecklist/UserWebsiteInput';
import WebsiteModal from 'app/common/react/components/settings/UserSettingsPage/WebsiteModal';
import withViewer from 'app/common/lib/react/withViewer';
import { compose } from 'redux';
import { track } from 'app/common/lib/react/analytics';
import { type AuthViewer } from 'app/packages/pinterest-flow-types/viewer';
import { type Router } from 'app/common/lib/ReactRouter/types';
import { type State as ReduxState } from 'app/common/redux/reducers/types';
import { withRouter } from 'react-router';
import { Box, Heading, IconButton } from 'gestalt';

type HOCProps = {|
  router: Router,
  viewer: AuthViewer,
|};

type ReduxStateProps = {|
  pinCreatedViaCreatorChecklist: boolean,
|};

type OwnProps = {|
  actionList: Array<string>,
  handleDismiss: () => void,
  header: string,
|};

type ConnectedProps = {|
  ...ReduxStateProps,
  ...OwnProps,
|};

type Props = {|
  ...HOCProps,
  ...ReduxStateProps,
  ...OwnProps,
|};

type ChecklistState = {|
  attemptToClaimWebsite: boolean,
  showUserWebsiteInputModal: boolean,
  websiteModalShown: boolean,
  websiteUrl: ?string,
|};

class CreatorChecklistHero extends Component<Props, ChecklistState> {
  state: ChecklistState = {
    attemptToClaimWebsite: Cookie.get(PCONST.EVENT_CLAIM_WEBSITE) === '1',
    showUserWebsiteInputModal: false,
    websiteModalShown: false,
    websiteUrl: this.props.viewer.domainUrl,
  };

  handleClaimWebsiteSuccess = () => {
    track(PCONST.thrift.EventType.CREATOR_CHECKLIST_SUBMIT_CLAIM_WEBSITE, {
      view: PCONST.thrift.ViewType.FEED,
      viewParameter: PCONST.thrift.ViewParameterType.FEED_HOME,
      component: PCONST.thrift.ComponentType.BUSINESS_ACCOUNT_CHECKLIST_HERO,
    });
    this.setState({
      attemptToClaimWebsite: Cookie.get(PCONST.EVENT_CLAIM_WEBSITE) === '1',
    });
  };

  closeUserWebsiteInputModal = () => this.setState({ showUserWebsiteInputModal: false });

  closeClaimWebsiteModal = () => {
    this.setState({
      websiteModalShown: false,
    });
  };

  handleWebsiteChange = (value: string) => {
    this.setState({ websiteUrl: value });
  };

  navigateToCreatePin = () => {
    const { router } = this.props;
    track(PCONST.thrift.EventType.CLICK, {
      view: PCONST.thrift.ViewType.FEED,
      viewParameter: PCONST.thrift.ViewParameterType.FEED_HOME,
      component: PCONST.thrift.ComponentType.BUSINESS_ACCOUNT_CHECKLIST_HERO,
      element: PCONST.thrift.ElementType.CHECKLIST_CREATE_PIN_BUTTON,
    });

    router.push('/pin-builder/');
  };

  openClaimWebsiteModal = () => {
    this.setState({
      websiteModalShown: true,
    });
    if (this.state.websiteUrl !== this.props.viewer.domainUrl) {
      const saveResource = resourceFactory.create('UserSettingsResource', {
        website_url: this.state.websiteUrl,
      });
      saveResource.callUpdate().finally(() => {
        Cookie.unset(PCONST.EVENT_CLAIM_WEBSITE);
      });
    }
  };

  showUserWebsiteInputModal = () => {
    track(PCONST.thrift.EventType.CLICK, {
      view: PCONST.thrift.ViewType.FEED,
      viewParameter: PCONST.thrift.ViewParameterType.FEED_HOME,
      component: PCONST.thrift.ComponentType.BUSINESS_ACCOUNT_CHECKLIST_HERO,
      element: PCONST.thrift.ElementType.CHECKLIST_CLAIM_WEBSITE_BUTTON,
    });
    this.setState({ showUserWebsiteInputModal: true });
  };

  renderChecklistAction = (
    item: string,
    width: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12,
  ) => {
    let actionTitle;
    switch (item) {
      case 'show_claim_website':
        const { attemptToClaimWebsite } = this.state;
        actionTitle = i18n._('Claim your website', 'Label for claiming website');
        return (
          <CreatorChecklistAction
            accessibilityLabel={actionTitle}
            iconType={attemptToClaimWebsite ? 'ellipsis' : 'globe'}
            iconBackgroundColor="white"
            iconColor="blue"
            onClickHander={this.showUserWebsiteInputModal}
            text={actionTitle}
            widthInColumn={width}
          />
        );
      case 'show_create_pin':
        const { pinCreatedViaCreatorChecklist } = this.props;
        actionTitle = i18n._('Create your first Pin', 'Label for creating the first pin');
        return (
          <CreatorChecklistAction
            accessibilityLabel={actionTitle}
            iconType={pinCreatedViaCreatorChecklist ? 'check' : 'pin'}
            iconBackgroundColor={pinCreatedViaCreatorChecklist ? 'green' : 'white'}
            iconColor={pinCreatedViaCreatorChecklist ? 'white' : 'blue'}
            onClickHander={this.navigateToCreatePin}
            text={actionTitle}
            widthInColumn={width}
          />
        );
      default:
        return null;
    }
  };

  render() {
    const { actionList, handleDismiss, header, viewer } = this.props;
    const { websiteModalShown, websiteUrl } = this.state;

    const itemColWidth = actionList && actionList.length ? Math.floor(12 / actionList.length) : 1;
    const headline =
      header ||
      i18n._(
        'Follow these steps to get going on Pinterest',
        "The headline of new business user's checklist",
      );

    return (
      <Box
        display="flex"
        justifyContent="between"
        height={150}
        color="lightGray"
        position="relative"
      >
        <Box
          color="blue"
          display="flex"
          alignItems="center"
          justifyContent="center"
          width="40%"
          minWidth={352}
          paddingX={8}
          paddingY={8}
          dangerouslySetInlineStyle={{ __style: { borderTopRightRadius: '240px 476px' } }}
        >
          <Heading color="white" size="sm">
            {headline}
          </Heading>
        </Box>

        <Box display="flex" width="60%" alignItems="center" justifyContent="center" paddingX={12}>
          {/* $FlowFixMe: itemColWidth is a calculated value instead of enum */}
          {actionList.map((item) => this.renderChecklistAction(item, itemColWidth))}
        </Box>

        <Box position="absolute" top right padding={2}>
          <IconButton
            accessibilityLabel={i18n._('Close', 'Close the experience UI')}
            icon="cancel"
            onClick={handleDismiss}
          />
        </Box>

        {this.state.showUserWebsiteInputModal && (
          <UserWebsiteInput
            onComplete={this.openClaimWebsiteModal}
            onDismiss={this.closeUserWebsiteInputModal}
            onWebsiteInputChange={this.handleWebsiteChange}
            websiteUrl={viewer.domainUrl}
          />
        )}

        {websiteModalShown && (
          <WebsiteModal
            onHide={this.closeClaimWebsiteModal}
            attemptToClaimWebsite={() => {}}
            onResourceError={() => {}}
            onSuccess={this.handleClaimWebsiteSuccess}
            websiteUrl={websiteUrl}
          />
        )}
      </Box>
    );
  }
}

function mapStateToProps({ creatorChecklist }: ReduxState) {
  if (creatorChecklist) {
    const { pinCreatedViaCreatorChecklist } = creatorChecklist;
    return { pinCreatedViaCreatorChecklist };
  }
  return { pinCreatedViaCreatorChecklist: false };
}

export default compose(
  connect<ConnectedProps, OwnProps, _, _, _, _>(
    mapStateToProps,
    () => ({}),
  ),
  withViewer,
  withRouter,
)(CreatorChecklistHero);
